import React, { useCallback, useEffect, useState } from "react";
import {
  MdAttachMoney,
  MdHourglassTop,
  MdReceiptLong,
  MdCheckCircle,
  MdAddCircleOutline,
  MdViewColumn,
  MdViewList,
  MdDashboard,
  MdMailOutline,
  MdAccountBalance,
} from "react-icons/md";
import Widget from "../../../components/widget/Widget";
import SkeletonTable from "../../../components/skeleton/table";
import api from "../../../services/axios";
import { Billing, WeeklyReportResult } from "../../../interfaces/Financial";
import BillingTable from "./components/BillingTable";
import ServiceOrderModal from "./components/ServiceOrderModal";
import ServiceOrderEditModal from "./components/ServiceOrderEditModal";
import FinancialKanban from "./components/FinancialKanban";
import FinancialOverview from "./components/FinancialOverview";
import InvoiceDocModal from "./components/InvoiceDocModal";
import ReportLogModal from "./components/ReportLogModal";
import ExtratoConciliacao from "./components/ExtratoConciliacao";
import { MdHistory } from "react-icons/md";
import { toast } from "react-toastify";

type ViewMode = "overview" | "list" | "kanban" | "conciliacao";

const FinancialManagement: React.FC = () => {
  const now = new Date();
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [year, setYear] = useState<number>(now.getFullYear());
  const [billings, setBillings] = useState<Billing[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<ViewMode>("overview");
  const [sendingReports, setSendingReports] = useState(false);
  const [showReportLog, setShowReportLog] = useState(false);
  const [showServiceOrderModal, setShowServiceOrderModal] = useState(false);
  const [editingServiceOrder, setEditingServiceOrder] = useState<Billing | null>(null);
  // Incrementa a cada mudança de cobrança para forçar o recarregamento da aba "Visão".
  const [overviewReload, setOverviewReload] = useState(0);
  const bumpOverview = () => setOverviewReload((n) => n + 1);
  const refreshAll = () => {
    fetchBillings();
    bumpOverview();
  };

  // `silent` recarrega sem o skeleton da tela — usado após ações otimistas (mover card no
  // kanban, ações da lista), para a navegação ficar fluida em vez de "piscar" a página.
  const fetchBillings = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const response = await api.get<Billing[]>("/Financial/Billings", {
        params: { month, year },
      });
      setBillings(response.data);
    } catch (error) {
      console.error("Error fetching billings:", error);
      if (!opts?.silent) setBillings([]);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchBillings();
  }, [fetchBillings]);

  // Fluxo de 3 fases (derivado no backend): 1 Pendente de NF · 2 Faturada · 3 Recebida.
  const totalAmount = billings
    .filter((b) => b.status !== 4)
    .reduce((sum, b) => sum + b.amount, 0);
  const pendingNfAmount = billings
    .filter((b) => b.phase === 1)
    .reduce((sum, b) => sum + b.amount, 0);
  const billedAmount = billings
    .filter((b) => b.phase === 2)
    .reduce((sum, b) => sum + b.amount, 0);
  const receivedAmount = billings
    .filter((b) => b.phase === 3)
    .reduce((sum, b) => sum + b.amount, 0);

  const fmt = (value: number) =>
    value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  ];

  const callBillingAction = async (
    billingId: number,
    nextPhase: number,
    url: string,
    successMsg: string
  ) => {
    // Otimista: o card muda de fase na hora; o refetch silencioso traz os campos derivados
    // (datas, nº da NF, status) sem trocar a tela pelo skeleton. Erro → refetch p/ rollback.
    setBillings((prev) =>
      prev.map((b) => (b.billingId === billingId ? { ...b, phase: nextPhase } : b))
    );
    try {
      await api.post(url);
      toast.success(successMsg, { position: "top-right", autoClose: 4000 });
      await fetchBillings({ silent: true });
      bumpOverview();
    } catch (err: any) {
      const msg = err?.response?.data;
      toast.error(typeof msg === "string" ? msg : "Operation failed.", {
        position: "top-right",
        autoClose: 4000,
      });
      fetchBillings({ silent: true });
      throw err;
    }
  };

  // Emitir NF passa sempre pelo modal: ele mostra os dados fiscais e, com o SIIMP ligado, a
  // discriminação e o vencimento que vão na nota — documento fiscal não sai sem revisão.
  const [invoiceTarget, setInvoiceTarget] = useState<Billing | null>(null);
  const handleEmitInvoice = async (billingId: number) => {
    setInvoiceTarget(billings.find((b) => b.billingId === billingId) ?? null);
  };

  const handleMarkReceived = (billingId: number) =>
    callBillingAction(billingId, 3, `/Financial/MarkAsReceived/${billingId}`, "Marcado como recebido.");

  const handleSendWeeklyReports = async () => {
    setSendingReports(true);
    try {
      const res = await api.post<WeeklyReportResult>("/Financial/SendWeeklyReports");
      const r = res.data;
      toast.success(
        `Reports da semana enviados: ${r.clientEmailsSent} cliente(s) e ${r.employeeEmailsSent} colaborador(es).`,
        { position: "top-right", autoClose: 5000 }
      );
    } catch (err: any) {
      const msg = err?.response?.data;
      toast.error(typeof msg === "string" ? msg : "Falha ao enviar os reports semanais.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setSendingReports(false);
    }
  };

  return (
    <section className="mt-5 flex animate-fade-up flex-col gap-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <span className="dv-aura flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow">
            <MdAttachMoney className="h-6 w-6" aria-hidden="true" />
          </span>
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
              Financeiro
            </span>
            <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
              Faturamento
            </h1>
          </div>
        </div>

        <div className="inline-flex self-start rounded-xl border border-gray-200 bg-white p-1 dark:border-white/10 dark:bg-navy-800 lg:self-auto">
          <button
            onClick={() => setViewMode("overview")}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              viewMode === "overview"
                ? "bg-dv-blue-600 text-white shadow-dv-sm dark:bg-dv-blue-500"
                : "text-gray-600 hover:text-navy-700 dark:text-gray-300 dark:hover:text-white"
            }`}
          >
            <MdDashboard className="h-4 w-4" aria-hidden="true" />
            Visão
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              viewMode === "list"
                ? "bg-dv-blue-600 text-white shadow-dv-sm dark:bg-dv-blue-500"
                : "text-gray-600 hover:text-navy-700 dark:text-gray-300 dark:hover:text-white"
            }`}
          >
            <MdViewList className="h-4 w-4" aria-hidden="true" />
            Lista
          </button>
          <button
            onClick={() => setViewMode("kanban")}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              viewMode === "kanban"
                ? "bg-dv-blue-600 text-white shadow-dv-sm dark:bg-dv-blue-500"
                : "text-gray-600 hover:text-navy-700 dark:text-gray-300 dark:hover:text-white"
            }`}
          >
            <MdViewColumn className="h-4 w-4" aria-hidden="true" />
            Kanban
          </button>
          <button
            onClick={() => setViewMode("conciliacao")}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              viewMode === "conciliacao"
                ? "bg-dv-blue-600 text-white shadow-dv-sm dark:bg-dv-blue-500"
                : "text-gray-600 hover:text-navy-700 dark:text-gray-300 dark:hover:text-white"
            }`}
          >
            <MdAccountBalance className="h-4 w-4" aria-hidden="true" />
            Conciliação
          </button>
        </div>
      </header>

      <div className="flex flex-col gap-4 rounded-[20px] border border-gray-100 bg-white p-4 nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="financial-month" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Mês
          </label>
          <select
            id="financial-month"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white sm:w-44"
          >
            {months.map((m, i) => (
              <option key={i + 1} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="financial-year" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Ano
          </label>
          <input
            id="financial-year"
            type="number"
            value={year}
            min={2020}
            max={2100}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white sm:w-28"
          />
        </div>

        <div className="hidden h-11 w-px self-end bg-gray-200 dark:bg-white/10 sm:block" />

        <div className="flex flex-1 flex-wrap items-end gap-2.5">
          <button
            onClick={() => setShowServiceOrderModal(true)}
            className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            <MdAddCircleOutline className="h-4 w-4" aria-hidden="true" />
            Gerar Faturamento
          </button>

          <button
            onClick={handleSendWeeklyReports}
            disabled={sendingReports}
            title="Envia o report de horas da última semana para clientes e colaboradores"
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
          >
            <MdMailOutline className="h-4 w-4" aria-hidden="true" />
            {sendingReports ? "Enviando..." : "Enviar reports da semana"}
          </button>

          <button
            onClick={() => setShowReportLog(true)}
            title="Ver o histórico de reports enviados"
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
          >
            <MdHistory className="h-4 w-4" aria-hidden="true" />
            Histórico de envios
          </button>
        </div>
      </div>

      {viewMode === "overview" ? (
        <div className="mt-4">
          <FinancialOverview month={month} year={year} reloadSignal={overviewReload} />
        </div>
      ) : viewMode === "conciliacao" ? (
        <div className="mt-4">
          <ExtratoConciliacao
            onChange={() => {
              fetchBillings({ silent: true });
              bumpOverview();
            }}
          />
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            <Widget
              icon={<MdAttachMoney className="h-7 w-7" />}
              title="Total"
              subtitle={fmt(totalAmount)}
            />
            <Widget
              icon={<MdHourglassTop className="h-7 w-7" />}
              title="Pendente de NF"
              subtitle={fmt(pendingNfAmount)}
            />
            <Widget
              icon={<MdReceiptLong className="h-7 w-7" />}
              title="Faturada"
              subtitle={fmt(billedAmount)}
            />
            <Widget
              icon={<MdCheckCircle className="h-7 w-7" />}
              title="Recebida"
              subtitle={fmt(receivedAmount)}
            />
          </div>

          <div className="mt-5">
            {loading ? (
              <SkeletonTable active={loading} />
            ) : viewMode === "list" ? (
              <BillingTable
                billings={billings}
                onRefresh={() => fetchBillings({ silent: true })}
                onEditServiceOrder={setEditingServiceOrder}
              />
            ) : (
              <FinancialKanban
                billings={billings}
                onEditServiceOrder={setEditingServiceOrder}
                onEmitInvoice={handleEmitInvoice}
                onMarkReceived={handleMarkReceived}
                onRefresh={() => fetchBillings({ silent: true })}
              />
            )}
          </div>
        </>
      )}

      {invoiceTarget && (
        <InvoiceDocModal
          billingId={invoiceTarget.billingId}
          clientId={invoiceTarget.clientId}
          clientName={invoiceTarget.clientName}
          amount={invoiceTarget.amount}
          billingType={invoiceTarget.billingType}
          monthRef={invoiceTarget.monthRef}
          yearRef={invoiceTarget.yearRef}
          onClose={() => setInvoiceTarget(null)}
          onSuccess={() => {
            fetchBillings({ silent: true });
            bumpOverview();
          }}
        />
      )}

      {showServiceOrderModal && (
        <ServiceOrderModal
          month={month}
          year={year}
          onClose={() => setShowServiceOrderModal(false)}
          onSuccess={refreshAll}
        />
      )}

      {editingServiceOrder && (
        <ServiceOrderEditModal
          billing={editingServiceOrder}
          onClose={() => setEditingServiceOrder(null)}
          onSuccess={refreshAll}
        />
      )}

      {showReportLog && <ReportLogModal onClose={() => setShowReportLog(false)} />}
    </section>
  );
};

export default FinancialManagement;
